import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import dayjs from 'dayjs';
import {
    WeeklyDirection,
    WeeklyObservationAreaV2,
    WeeklyObservationsV2
} from '@shared/api/models/query/patient-weekly-snapshot.model';
import {WeeklyObservationLabels} from '@cdss/pages/patient-overview-v2/detail/weekly/weekly-carer-copy';
import {WeeklyDirectionBadgeComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-direction-badge/weekly-direction-badge.component';
import {WeeklyObservationTrendBarComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-observation-trend-bar/weekly-observation-trend-bar.component';

const HISTORY_WEEKS = 8;

interface ObservationRow {
    key: keyof WeeklyObservationsV2;
    label: string;
    direction: WeeklyDirection | null;
    history: (WeeklyDirection | null)[];
    pointLabels: string[];
}

const AREA_ORDER: (keyof WeeklyObservationsV2)[] = [
    'eatingAtMealtimes',
    'finishingPortions',
    'varietyOfFoods',
    'anxietyAroundFood',
    'overallProgress',
];

/**
 * Carer weekly summary — "Your observations" panel (UNIVAPLAT-3038, ticket
 * description). Like its sibling `weekly-how-its-going`, this component owns its
 * own title + subtitle per the ticket's explicit file-level description ("title
 * 'Your observations' bold + muted 'vs last week · 8-week trend'") rather than
 * staying "dumb" like the other v2 panels — deliberate, ticket-specified.
 *
 * Renders the five observation areas (`WeeklyObservationsV2`, fixed order per the
 * ticket), each with this week's `direction` (`better`/`noChange`/`worse`) via the
 * shared direction badge and an 8-slot `history` trend via
 * `cdss-weekly-observation-trend-bar` (UNIVAPLAT-2360 follow-up — design correction:
 * discrete per-day markers on a flat baseline, not the continuous line chart used
 * for the ED-15 overall score).
 *
 * Per HAZ-W4 (Epic UNIVAPLAT-2248 hazard log, comment 34113): a `null` week in
 * `history` is a genuine gap (no check-in that week) and must never be conflated
 * with a `noChange` value. `null` is passed through to the trend bar unchanged —
 * it renders a plain baseline segment with no marker for both `noChange` and
 * `null`, distinguished only via its aria-label ("no change" vs "not answered"),
 * so this component does no coalescing of its own before plotting.
 *
 * The model's `history` doc comment guarantees the last chronological entry
 * equals `direction`; `toRow` defensively re-asserts that when building the
 * plotted array so the trend bar's rightmost slot and the direction badge can
 * never visually disagree even if the payload were ever inconsistent.
 *
 * `observations === null` (no check-in at all this week) renders all five rows
 * with an all-gap 8-week history and a null `direction` — the shared direction
 * badge already renders "No check-in" for a null direction — rather than a single
 * collapsed whole-panel empty state or (worse) flat zero-value graphs.
 */
@Component({
    selector: 'cdss-weekly-observations',
    standalone: true,
    imports: [CommonModule, WeeklyDirectionBadgeComponent, WeeklyObservationTrendBarComponent],
    templateUrl: './weekly-observations.component.html',
    styleUrl: './weekly-observations.component.scss'
})
export class WeeklyObservationsComponent {

    @Input() observations: WeeklyObservationsV2 | null = null;
    @Input() labels!: WeeklyObservationLabels;

    get rows(): ObservationRow[] {
        return AREA_ORDER.map(key => this.toRow(key, this.observations?.[key] ?? null));
    }

    trackByKey(_index: number, row: ObservationRow): string {
        return row.key;
    }

    private toRow(key: keyof WeeklyObservationsV2, area: WeeklyObservationAreaV2 | null): ObservationRow {
        if (!area) {
            return {
                key,
                label: this.labels[key],
                direction: null,
                history: new Array(HISTORY_WEEKS).fill(null),
                pointLabels: [],
            };
        }
        const sortedKeys = Object.keys(area.history).sort();
        const history = sortedKeys.map(k => area.history[k]);
        // Cross-check (see class doc): force the last plotted week to match this
        // week's direction badge rather than trusting the two payload fields to
        // independently agree.
        if (history.length > 0) {
            history[history.length - 1] = area.direction;
        }
        return {
            key,
            label: this.labels[key],
            direction: area.direction,
            history,
            pointLabels: sortedKeys.map(k => this.weekLabelFromYmd(k)),
        };
    }

    private weekLabelFromYmd(key: string): string {
        const iso = key.length === 8
            ? `${key.substring(0, 4)}-${key.substring(4, 6)}-${key.substring(6, 8)}`
            : key;
        const parsed = dayjs(iso);
        return parsed.isValid() ? parsed.format('D MMM') : key;
    }
}
